import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ChevronRight } from 'lucide-react';
import { TopBanner } from '../components/TopBanner';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Modal } from '../components/ui/Modal';
import { HubSpotForm } from '../components/HubSpotForm';
// Placeholder data for the blog listing
const blogPosts = [{
  title: 'Caravan and Boat Storage North of Perth: Secure Options in Neerabup & Bullsbrook',
  slug: 'caravan-boat-storage-neerabup-bullsbrook',
  date: 'January 24, 2025',
  readingTime: '8 min read',
  image: 'https://cdn.magicpatterns.com/uploads/4H7vMpv3GeDGEYCVvASyPc/Hold_my_van_hero.jpg',
  excerpt: "Discover why Neerabup and Bullsbrook are becoming the go-to solutions for secure caravan and boat storage in Perth's northern corridor.",
  category: 'Storage Insights'
}, {
  title: 'How to Prepare Your Caravan for Storage',
  slug: 'prepare-caravan-for-storage',
  date: 'January 25, 2025',
  readingTime: '10 min read',
  image: 'https://cdn.magicpatterns.com/uploads/4H7vMpv3GeDGEYCVvASyPc/Hold_my_van_hero.jpg',
  excerpt: 'A no-nonsense pre-storage checklist for Perth caravan owners. Learn how to protect your investment and avoid common storage mistakes.',
  category: 'Maintenance Guide'
}];
export function BlogPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  return <div className="min-h-screen flex flex-col font-sans" data-id="element-473">
      <Helmet data-id="element-474">
        {/* Preconnect to external domains */}
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-475" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-476" />
        <link rel="preconnect" href="https://cdn.magicpatterns.com" data-id="element-477" />

        <title data-id="element-478">Blog & Resources | Hold My Van</title>
        <meta name="description" content="Expert advice, tips, and insights on caravan and boat storage in Perth. Stay updated with Hold My Van." data-id="element-479" />
        <link rel="canonical" href="https://holdmyvan.com.au/blog" data-id="element-480" />
      </Helmet>

      <TopBanner data-id="element-481" />
      <Header onJoinClick={openModal} data-id="element-482" />

      <main className="flex-grow bg-gray-50" data-id="element-483">
        {/* Page Header */}
        <div className="bg-[#175445] py-20 text-center text-[#fbf7dd] relative overflow-hidden" data-id="element-484">
          <div className="absolute inset-0 bg-black/10" data-id="element-485"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4" data-id="element-486">
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-487">
              Blog & Resources
            </h1>
            <p className="text-xl text-gray-100 max-w-2xl mx-auto" data-id="element-488">
              Expert advice, industry insights, and tips for caravan and boat
              owners in Perth.
            </p>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-489">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-490">
            {blogPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group bg-[#fbf7dd] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full" data-id="element-491">
                <div className="relative h-56 overflow-hidden" data-id="element-492">
                  <div className="absolute top-4 left-4 bg-[#FFC107] text-black text-xs font-bold px-3 py-1 rounded-full z-10 uppercase tracking-wide" data-id="element-493">
                    {post.category}
                  </div>
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" data-id="element-494" />
                </div>

                <div className="p-6 flex-grow flex flex-col" data-id="element-495">
                  <div className="flex items-center text-xs text-gray-500 mb-4 space-x-4" data-id="element-496">
                    <div className="flex items-center" data-id="element-497">
                      <Calendar className="h-3 w-3 mr-1 text-[#175445]" data-id="element-498" />
                      {post.date}
                    </div>
                    <div className="flex items-center" data-id="element-499">
                      <Clock className="h-3 w-3 mr-1 text-[#175445]" data-id="element-500" />
                      {post.readingTime}
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#175445] transition-colors line-clamp-2" data-id="element-501">
                    {post.title}
                  </h2>

                  <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-grow" data-id="element-502">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center text-[#175445] font-bold text-sm mt-auto" data-id="element-503">
                    Read Article{' '}
                    <ChevronRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" data-id="element-504" />
                  </div>
                </div>
              </Link>)}

            {/* Placeholder Card for "Coming Soon" */}
            <div className="bg-gray-100 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-8 text-center min-h-[400px]" data-id="element-505">
              <div className="bg-white p-4 rounded-full shadow-sm mb-4" data-id="element-506">
                <Calendar className="h-8 w-8 text-gray-400" data-id="element-507" />
              </div>
              <h3 className="text-lg font-bold text-gray-500 mb-2" data-id="element-508">
                More Articles Coming Soon
              </h3>
              <p className="text-gray-400 text-sm max-w-xs" data-id="element-509">
                We're working on more guides and tips for caravan and boat
                owners. Check back soon!
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-510" />

      <Modal isOpen={isModalOpen} onClose={closeModal} data-id="element-511">
        <div className="text-center mb-6" data-id="element-512">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-513">
            Join the Priority List
          </h3>
          <p className="text-gray-600 mb-2" data-id="element-514">
            Early registrants will receive priority access to available storage
            bays and introductory pricing. Lock in early bird pricing.
          </p>
          <p className="text-sm text-gray-500 italic" data-id="element-515">
            Registering your interest does not commit you to a booking.
          </p>
        </div>
        <HubSpotForm data-id="element-516" />
      </Modal>
    </div>;
}